import { useEffect, useState } from "react";
import { errorMessage, request, RequestError } from "../lib/api";
import { getToken } from "../lib/auth";
import type { TeacherPDDaySummary } from "../types";

export interface TeacherPDDaySummaryState {
  data: TeacherPDDaySummary | null;
  loading: boolean;
  error: string;
}

const emptyState: TeacherPDDaySummaryState = { data: null, loading: true, error: "" };
let cacheToken: string | null = null;
let summaryCache = new Map<string, TeacherPDDaySummaryState>();

function currentCache(): Map<string, TeacherPDDaySummaryState> {
  const token = getToken();
  if (token !== cacheToken) {
    cacheToken = token;
    summaryCache = new Map();
  }
  return summaryCache;
}

async function loadSummary(sessionID: string): Promise<TeacherPDDaySummaryState> {
  const token = getToken();
  try {
    const data = await request<TeacherPDDaySummary>(`/api/teacher/pd-days/${sessionID}`);
    const next = { data, loading: false, error: "" };
    if (getToken() === token) currentCache().set(sessionID, next);
    return next;
  } catch (cause) {
    const saved = currentCache().get(sessionID);
    const next =
      saved?.data && !(cause instanceof RequestError && cause.status === 401)
        ? saved
        : { data: null, loading: false, error: errorMessage(cause) };
    if (getToken() === token) currentCache().set(sessionID, next);
    return next;
  }
}

// A tapped row waits for its first summary before changing routes, so the
// list stays in place instead of being replaced by a loading screen.
export async function prepareTeacherPDDaySummary(sessionID: string): Promise<void> {
  if (currentCache().get(sessionID)?.data) return;
  await loadSummary(sessionID);
}

export function useTeacherPDDaySummary(sessionID: string): TeacherPDDaySummaryState {
  const [state, setState] = useState<TeacherPDDaySummaryState>(
    () => currentCache().get(sessionID) ?? emptyState
  );

  useEffect(() => {
    let active = true;
    void loadSummary(sessionID).then((next) => {
      if (active) setState(next);
    });
    return () => {
      active = false;
    };
  }, [sessionID]);

  return state;
}
